import React from "react";
import { X, Plus, HelpCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

function IconChip({ item, options, onPick, onRemove, disabled, testId }) {
  const names = options.includes(item.name) ? options : [item.name, ...options];
  return (
    <span className={`inline-flex items-center rounded-md border ${item.sure ? "border-border" : "border-amber-500/70 bg-amber-950/30"}`} data-testid={testId}>
      <Select value={item.name} onValueChange={onPick} disabled={disabled}>
        <SelectTrigger className="h-7 border-0 bg-transparent text-xs gap-1 px-2 focus:ring-0" data-testid={`${testId}-select`}>
          {!item.sure && <HelpCircle className="w-3 h-3 text-amber-300" />}
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {names.map((n) => <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>)}
        </SelectContent>
      </Select>
      <button type="button" onClick={onRemove} disabled={disabled} className="px-1 text-muted-foreground hover:text-rose-300" data-testid={`${testId}-remove`}><X className="w-3 h-3" /></button>
    </span>
  );
}

/** Icons read from a screenshot (advantages / weapon qualities) as one-click dropdowns. Amber = the LLM wasn't sure. */
export function IconEditor({ card, onChange, disabled }) {
  const opts = card.icon_options || { advantage: [], quality: [] };
  const setItems = (gi, items) => onChange({ ...card, icons: card.icons.map((g, j) => (j === gi ? { ...g, items } : g)) });
  // Every correction is logged (all workgroups) so the screenshot reader learns from past mistakes.
  const learn = (g, payload) =>
    api.recordIconCorrection({ image: card.image_paths?.[0] || "", kind: g.kind, on: g.on, ...payload }).catch((e) => console.warn("icon correction not saved", e));
  const pick = (gi, ii, name) => {
    const g = card.icons[gi];
    const it = g.items[ii];
    if (it.read_as) learn(g, { read_as: it.read_as, right: name });
    else { learn(g, { right: it.name, remove: true }); learn(g, { right: name }); }
    setItems(gi, g.items.map((x, k) => (k === ii ? { ...x, name, sure: true } : x)));
  };
  const remove = (gi, ii) => {
    const g = card.icons[gi];
    const it = g.items[ii];
    learn(g, it.read_as ? { read_as: it.read_as, right: null } : { right: it.name, remove: true });
    setItems(gi, g.items.filter((_, k) => k !== ii));
  };
  const add = (gi, name) => {
    const g = card.icons[gi];
    learn(g, { right: name });
    setItems(gi, [...g.items, { name, sure: true, read_as: null }]);
  };
  const unsure = card.icons.some((g) => g.items.some((i) => !i.sure));
  return (
    <div className="rounded border border-border bg-background/40 p-3 space-y-2" data-testid="card-icon-editor">
      <p className="text-xs text-muted-foreground">
        <strong className="text-foreground">Icons read from the screenshot</strong> — pick the right one if a guess is wrong.
        {unsure && <span className="text-amber-300 ml-1">Amber icons are guesses.</span>}
        <span className="ml-1">Your corrections teach the reader for everyone.</span>
      </p>
      {card.icons.map((g, gi) => (
        <div key={`${g.on}-${gi}`} className="flex items-center gap-1.5 flex-wrap" data-testid={`card-icon-group-${gi}`}>
          <span className="text-xs text-muted-foreground min-w-[140px]">{g.on || "Model"} · {g.kind === "advantage" ? "advantages" : "weapon qualities"}</span>
          {g.items.map((it, ii) => (
            <IconChip
              key={`${it.name}-${ii}`}
              item={it}
              options={opts[g.kind] || []}
              disabled={disabled}
              testId={`card-icon-${gi}-${ii}`}
              onPick={(name) => pick(gi, ii, name)}
              onRemove={() => remove(gi, ii)}
            />
          ))}
          <Select value="" onValueChange={(name) => add(gi, name)} disabled={disabled}>
            <SelectTrigger className="h-7 w-auto text-xs gap-1 px-2 text-muted-foreground" data-testid={`card-icon-${gi}-add`}>
              <Plus className="w-3 h-3" /> <SelectValue placeholder="Add icon" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {(opts[g.kind] || []).filter((n) => !g.items.some((x) => x.name === n)).map((n) => (
                <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
